/**
 * New habit (Habits UX pass 2026-09-06). A title is enough; the rest sits
 * under two named groups — "Helps me plan it" (when it comes round, how long,
 * what it belongs to or which parts of life, priority) and "Helps me keep
 * you going" (the bare minimum, the cue, why it matters). Reminders live on
 * the habit page. The form offers what the habit page's Add row offers, and
 * nothing else.
 */
import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Repeat } from 'lucide-react'
import { HierarchyCreationForm, type BasicField, type FieldGroup } from '../components/hierarchy/HierarchyCreationForm'
import { HabitScheduleQuick } from '../components/habits/HabitScheduleQuick'
import { HabitService } from '../services/habitService'
import { useToast } from '../components/ui/Toast'
import { useLifeAreaOptions } from '../hooks/useLifeAreas'
import { LIFE_AREA_OPTIONS, lifeAreaLabel, lifeAreaValue } from '../utils/taskForm'
import { HABIT_PRIORITY_OPTIONS, DURATION_CHIPS } from '../utils/habitOptions'
import { habitFormToPayload, type HabitFormData } from '../utils/habitForm'

const PLAN_GROUP: FieldGroup = { key: 'plan', title: 'Helps me plan it', blurb: 'When it comes round and how long it takes. Skip what you do not know yet.' }
const KEEP_GROUP: FieldGroup = { key: 'keep', title: 'Helps me keep you going', blurb: 'What carries it on a hard day, in your own words.' }

const FIELDS: BasicField[] = [
  { name: 'title', label: 'What is the habit?', type: 'text', required: true, placeholder: 'e.g., Walk after dinner', section: 'basic' },
  {
    name: 'schedule', label: 'When it comes round', type: 'custom', group: PLAN_GROUP, section: 'basic',
    render: (formData, onFormDataChange) => <HabitScheduleQuick formData={formData} onFormDataChange={onFormDataChange} />,
  },
  { name: 'duration_minutes', span: 'half', label: 'Each time takes', type: 'chips', options: DURATION_CHIPS, numeric: true, group: PLAN_GROUP, section: 'basic' },
  { name: 'priority', span: 'half', label: 'Priority', type: 'chips', options: HABIT_PRIORITY_OPTIONS, group: PLAN_GROUP, section: 'basic' },
  // Skills hold habits too (the ratified tree; prod 2026-09-12: three of eight sit under a skill).
  { name: 'parent_link', span: 'half', label: 'Belongs to', type: 'parent_picker', allowedParentTypes: ['goal', 'skill'], group: PLAN_GROUP, section: 'basic' },
  {
    name: 'life_areas', label: 'Or, if it is just for you — which parts of life?', type: 'chips', multi: true, options: LIFE_AREA_OPTIONS,
    allowCustom: true, customLabel: 'Another…', customPlaceholder: 'e.g. Side project', customValue: lifeAreaValue, labelOf: lifeAreaLabel,
    helpText: 'Pick one or more; the first is the main one. Name your own if none of these fits.',
    showWhen: (f) => !f.parent_id, group: PLAN_GROUP, section: 'basic',
  },
  { name: 'minimum_viable_repetition', span: 'half', label: 'Bare minimum', type: 'text', placeholder: 'The smallest version that still counts — one page, five minutes', group: KEEP_GROUP, section: 'basic' },
  { name: 'cue_or_anchor', span: 'half', label: 'Cue', type: 'text', placeholder: 'What it follows — after the first coffee', group: KEEP_GROUP, section: 'basic' },
  { name: 'description', label: 'Why it matters', type: 'text', placeholder: 'What this habit means to you — one line is plenty', group: KEEP_GROUP, section: 'basic' },
]

export const CreateHabit: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const seededTitle = (location.state as { title?: string } | null)?.title || ''
  const [formData, setFormData] = useState<HabitFormData>({ title: seededTitle, priority: 'medium', parent_type: '', parent_id: null })
  const [formError, setFormError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const habitService = useMemo(() => new HabitService(), [])

  const picked: string[] = Array.isArray(formData.life_areas) ? formData.life_areas.map(String) : []
  const { options: lifeAreaOptions, remember: rememberLifeArea } = useLifeAreaOptions(picked)
  const fields = useMemo(() => FIELDS.map((f) => (f.name === 'life_areas' ? { ...f, options: lifeAreaOptions } : f)), [lifeAreaOptions])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (!String(formData.title || '').trim()) {
      setFormError('Give it a name — that is the one thing I need.')
      return
    }
    setIsLoading(true)
    try {
      const created = await habitService.createHabit(habitFormToPayload(formData))
      picked.forEach(rememberLifeArea)
      toast.success('Habit added', created.title)
      navigate(created?.id ? `/habits/${created.id}` : '/habits')
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add the habit')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <HierarchyCreationForm
      title="New habit"
      description="A name is enough. Everything else can be added on the habit itself, whenever you know it."
      icon={<Repeat className="h-8 w-8 text-brand" />}
      basicFields={fields}
      advancedFields={[]}
      compactHeader
      formData={formData}
      onFormDataChange={setFormData}
      onSubmit={handleSubmit}
      submitLabel="Save"
      error={formError}
      primaryColor="indigo"
      backUrl="/habits"
      isLoading={isLoading}
    />
  )
}

export default CreateHabit
